import { Component, useState } from 'react';
import CookingModule from '../../features/cooking/CookingModule';
import MoviesModule from '../../features/movies/MoviesModule';
import ReadingModule from '../../features/reading/ReadingModule';
import TravelingModule from '../../features/traveling/TravelingModule';
import TVShowsModule from '../../features/tvshows/TVShowsModule';
import FitnessModule from '../../features/fitness/FitnessModule';
import HikingModule from '../../features/hiking/HikingModule';
import MusicModule from '../../features/music/MusicModule';
import DiningModule from '../../features/dining/DiningModule';
import CreativeModule from '../../features/creative/CreativeModule';
import { workspaceStrings } from '../../content/workspace';
import { getPublicErrorMessage } from '../../utils/errors';

// 1. Registry of Hobby Modules
const HOBBY_COMPONENTS = {
  cooking: CookingModule,
  movies: MoviesModule,
  reading: ReadingModule,
  traveling: TravelingModule,
  tvshows: TVShowsModule,
  fitness: FitnessModule,
  hiking: HikingModule,
  music: MusicModule,
  dining: DiningModule,
  creative: CreativeModule,
};

class HobbyModuleErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  /**
   * Retrieves derived state from error.
   *
   * @param {*} error - Failure being normalized or reported.
   * @returns {*} The derived operation result.
   */
  static getDerivedStateFromError(error) {
    return { error };
  }

  /**
   * Performs the component did update operation.
   *
   * @param {*} previousProps - previous props value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  componentDidUpdate(previousProps) {
    if (previousProps.hobbyId !== this.props.hobbyId && this.state.error) {
      this.setState({ error: null });
    }
  }

  /**
   * Renders the requested operation.
   *
   * @returns {JSX.Element} The derived operation result.
   */
  render() {
    if (this.state.error) {
      return (
        <div className="empty-state module-error-state">
          <p>{workspaceStrings.moduleLoadFailed}</p>
          <small>{getPublicErrorMessage(this.state.error)}</small>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Renders the workspace experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.hobby - hobby value.
 * @param {*} props.modulePath - module path value.
 * @returns {JSX.Element} The rendered interface.
 */
export default function Workspace({ hobby, modulePath }) {
  const [isAdding, setIsAdding] = useState(false);
  const [closeGuard, setCloseGuard] = useState(null);
  const [headerActions, setHeaderActions] = useState(null);
  const [headerTitle, setHeaderTitle] = useState('');
  const SelectedModule = HOBBY_COMPONENTS[hobby.id];
  /**
   * Handles header click.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleHeaderClick = () => {
    if (closeGuard) {
      closeGuard();
    }
  };

  return (
    <section className="hobby-workspace" key={hobby.id}>
      <header className="workspace-header">
        <button
          type="button"
          className="workspace-title-group workspace-title-button"
          onClick={handleHeaderClick}
          aria-label={workspaceStrings.overviewLabel(hobby.name)}
        >
          <div className="brand-dot" style={{ background: hobby.theme }}></div>
          <div>
            <h1>{headerTitle || hobby.name}</h1>
          </div>
        </button>
        {headerActions && <div className="workspace-actions">{headerActions}</div>}
      </header>

      <div className="workspace-content">
        {SelectedModule ? (
          <HobbyModuleErrorBoundary hobbyId={hobby.id}>
            <SelectedModule
              isAdding={isAdding}
              setIsAdding={setIsAdding}
              theme={hobby.theme}
              modulePath={modulePath}
              onClose={() => setIsAdding(false)}
              onRegisterCloseGuard={(handler) => setCloseGuard(() => handler)}
              onHeaderActionsChange={setHeaderActions}
              onHeaderTitleChange={setHeaderTitle}
            />
          </HobbyModuleErrorBoundary>
        ) : (
          <div className="empty-state">
            <p>{workspaceStrings.comingSoon(hobby.name)}</p>
          </div>
        )}
      </div>
    </section>
  );
}
